<template>
  <div
    class="overflow-y-auto"
    :class="localDataObj.length == 0 ? 'flex flex-row justify-center' : ''"
  >
    <div
      class="flex flex-col mt-20"
      style="height: 150px; width: 250px"
      v-if="localDataObj.length == 0 && !readOnly"
    >
      <div
        class="border-2 border-dashed border-surface-200 dark:border-surface-700 rounded bg-surface-50 dark:bg-surface-950 flex-auto flex justify-center items-center font-medium cursor-pointer"
        @click="
          () => {
            showSignDialog = true;
          }
        "
      >
        <i class="pi pi-plus text-2xl mr-2"></i>
        {{ localDataObj.length > 0 ? `Add More Signatures` : `Add Signatures` }}
      </div>
    </div>
    <div
      class="flex w-full justify-end"
      v-else-if="!readOnly"
    >
      <Button
        label="Add More"
        severity="secondary"
        icon="pi pi-plus"
        @click="
          () => {
            showSignDialog = true;
          }
        "
      />
    </div>
    <DataView
      lazy
      dataKey="id"
      :value="localDataObj"
      layout="grid"
      v-if="localDataObj.length > 0"
    >
      <template #grid="slotProps">
        <!-- Grid container with responsive column setup -->
        <div
          class="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-3 xl:grid-cols-3 gap-3 pt-3"
        >
          <div
            v-for="(item, index) in slotProps.items"
            :key="index"
            class="w-full border rounded p-2 relative shadow-xs"
          >
            <div class="flex items-center justify-between pb-2">
              <div class="flex items-center">
                <p class="pl-3">
                  <span class="font-semibold text-gray-800">
                    {{ item.attendeeName }}
                    <span
                      v-if="item.attendeeType"
                      class="text-sm font-semibold text-gray-500 ml-2"
                    >
                      {{ item.attendeeType }}
                    </span>
                    <span
                      v-else
                      class="text-sm font-semibold text-gray-500 ml-2"
                    >
                      Not Specified
                    </span>
                  </span>
                </p>
              </div>
              <!-- Remove Button -->
              <Button
                v-if="!readOnly"
                icon="pi pi-times"
                class="p-button-text p-button-sm text-red-500"
                @click="removeItem(index)"
                aria-label="Remove"
              />
            </div>
            <SignatureImg
              :signature="item"
              class="object-cover w-full"
            />
          </div>
        </div>
      </template>
    </DataView>
  </div>
  <SignatureDialog
    v-model:visible="showSignDialog"
    @onConfirm="onConfirmSignature"
  />
</template>

<script>
  import { defineComponent, ref, watch } from "vue";
  import SignatureDialog from "@/components/SignatureDialog.vue";
  import SignatureImg from "@/components/SignatureImg.vue";
  import Helper from "@/utils/helper";
  import { useFormTemplateResponseStore } from "@/stores/formTemplateResponseStore";
  export default defineComponent({
    name: "FlraAttendees",
    components: { SignatureDialog, SignatureImg },
    props: {
      dataObj: {
        type: Array,
        required: true,
        default: () => []
      },
      pageId: {
        type: String,
        required: false,
        default: null
      },
      templateId: {
        type: String,
        required: false,
        default: null
      },
      readOnly: {
        type: Boolean,
        default: false
      }
    },
    setup(props, { emit }) {
      const showSignDialog = ref(false);
      const selectedQuestObj = ref({});
      const formTemplateResponseStore = useFormTemplateResponseStore();

      // Create a local reactive copy of the dataObj prop
      const localDataObj = ref([...props.dataObj]);

      // Function to save data to the response store
      const saveToResponseStore = (data) => {
        if (props.pageId) {
          const templateId =
            props.templateId || formTemplateResponseStore.templateId;

          if (templateId) {
            // Get the current response for this template
            const currentTemplateResponse =
              formTemplateResponseStore.response[templateId] || {};

            // Update the specific page data
            const updatedTemplateResponse = {
              ...currentTemplateResponse,
              [props.pageId]: data
            };

            // Save the updated template response back to the store
            formTemplateResponseStore.setResponse(
              templateId,
              updatedTemplateResponse
            );
          }
        }
      };

      // Watch the props.dataObj and update the local copy if it changes
      watch(
        () => props.dataObj,
        (newVal) => {
          localDataObj.value = [...newVal];
          // Also save to response store when dataObj changes (e.g., when loading from database)
          saveToResponseStore(localDataObj.value);
        },
        { immediate: true } // Ensure this runs on component mount
      );

      const onConfirmSignature = (data) => {
        if (
          localDataObj.value.some(
            (item) =>
              item.attendeeName.trim().toLowerCase() ===
              data.attendeeName.trim().toLowerCase()
          )
        ) {
          Helper.showToast("Error", "Attendee already exists", "error");
          return;
        }

        // Add the new data to the local reactive array
        localDataObj.value.push(data);

        // Save to response store
        saveToResponseStore(localDataObj.value);

        // Emit update to parent component
        emit("update:dataObj", localDataObj.value);
      };

      const removeItem = (index) => {
        localDataObj.value.splice(index, 1);

        // Save to response store
        saveToResponseStore(localDataObj.value);

        // Emit update to parent component
        emit("update:dataObj", localDataObj.value);
      };

      return {
        SignatureDialog,
        selectedQuestObj,
        localDataObj, // Return the local reactive array
        onConfirmSignature,
        showSignDialog,
        removeItem,
        Helper
      };
    }
  });
</script>
